import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit';
import { format } from 'date-fns';
import { GetStaticPaths, GetStaticProps } from 'next';
import { ArticleJsonLd } from 'next-seo';
import { useRouter } from 'next/router';
import SbEditable from 'storyblok-react';

import { DynamicPage, AuthorCard, Heading } from '~/components';
import { BannerDynamic } from '~/components/Banners/BannerDynamic';
import { HeaderSimple } from '~/components/Blocks/HeaderSimple';
import { RelatedArticleCard } from '~/components/Blog';
import {
  addFormFieldsToStory,
  classNames,
  enhancedStaticProps,
  getCanonical,
  getDescription,
  getSlugWithLocale,
  sdk as sdkClient,
  withLayout,
} from '~/lib';
import { FooterComponent, NavigationComponent } from '../../graphql/sdk';

export interface BlogPostProps extends WithStoryProps {
  footer: FooterComponent;
  navigation: NavigationComponent;
}

const BlogPost = ({ story }: BlogPostProps) => {
  const { locale } = useRouter();

  const author = story?.content?.author?.content
    ? `${story?.content?.author?.content?.name}`
    : '';

  return (
    <div>
      <ArticleJsonLd
        url={getCanonical(story, locale)}
        title={story?.content?.title}
        description={getDescription(story)}
        datePublished={story?.first_published_at}
        dateModified={story?.published_at}
        images={
          story?.content?.thumbnail?.filename
            ? [story?.content?.thumbnail?.filename]
            : []
        }
        authorName="Instant Commerce"
        publisherName="Instant Commerce"
        publisherLogo=""
      />

      {story?.content?.title && (
        <HeaderSimple
          first
          overviewLink="/blog"
          subtext={`${author}
            ${
              !!story?.published_at
                ? format(new Date(story?.published_at), ' • dd MMMM yyyy')
                : ''
            }`}
          content={{
            title: story?.content?.title,
            description: story?.content?.subtitle,
          }}
        />
      )}

      <DynamicPage story={story} />

      {story?.content?.related_articles?.length > 0 && (
        <div className="w-full max-w-6xl mx-auto px-4 pb-20">
          <Heading variant="h4" as="h4">
            Related articles
          </Heading>
          <div className="grid grid-cols-1 gap-y-12 md:gap-y-0 md:grid-cols-3 md:gap-x-12 mt-10">
            {story?.content?.related_articles?.map((article) => (
              <RelatedArticleCard article={article} />
            ))}
          </div>
        </div>
      )}

      {story?.content?.banner?.content && (
        <SbEditable content={story.content.banner.content}>
          <div
            className={classNames(
              'w-full max-w-6xl mx-auto px-4 pt-0 lg:pt-0 lg:max-w-6xl',
            )}
          >
            <BannerDynamic content={story.content.banner.content} />
          </div>
        </SbEditable>
      )}

      {story?.content?.author?.content && (
        <div className="w-full bg-yellow-100 py-14 pt-56 -mt-40">
          <AuthorCard content={story.content.author.content} />
        </div>
      )}
    </div>
  );
};

export default withLayout(withStory(BlogPost));

export const getStaticProps: GetStaticProps = enhancedStaticProps(
  async ({ params: { slug }, sdk, locale, defaultLocale }) => {
    let story;
    let notFound = false;

    try {
      story = (
        await sdk.blogPostItem({
          slug: getSlugWithLocale(`blog/${slug}`, locale, defaultLocale),
          lang: locale,
        })
      ).BlogpostItem;
      story = await addFormFieldsToStory(story);
    } catch (e) {
      notFound = true;
    }

    return {
      props: {
        story,
      },
      notFound: notFound || !story,
      revalidate: 60,
    };
  },
);

export const getStaticPaths: GetStaticPaths = async () => {
  let stories;

  try {
    stories = (await sdkClient.blogPostItems()).BlogpostItems.items;
  } catch (e) {
    console.log(e);
  }

  return {
    paths: stories?.map(({ slug }) => ({
      params: { slug },
    })),
    fallback: 'blocking',
  };
};
